import { ANNOTATION_COLOURS, arrowHead, type PhotoAnnotation } from '@qms360/shared';

/**
 * The marks drawn on a photo, laid over it (pass 8). Positions are fractions of
 * the photo, so this must sit exactly over the image box: its parent is
 * `relative inline-block` around an image with no letterboxing. The PDF draws
 * the same marks the same way (`annotationOverlay` in the API's templates).
 */
export function AnnotationOverlay({ annotation }: { annotation: PhotoAnnotation | null }) {
  if (!annotation || annotation.marks.length === 0) return null;
  const at = ([x, y]: [number, number]) => `${x * 100},${y * 100}`;
  return (
    <>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        {annotation.marks.map((mark, index) => {
          const stroke = {
            fill: 'none',
            stroke: ANNOTATION_COLOURS[mark.colour],
            strokeWidth: 3,
            strokeLinecap: 'round' as const,
            strokeLinejoin: 'round' as const,
            vectorEffect: 'non-scaling-stroke' as const,
          };
          if (mark.kind === 'pen') return <polyline key={index} points={mark.points.map(at).join(' ')} {...stroke} />;
          if (mark.kind === 'arrow') {
            const [left, right] = arrowHead(mark.from, mark.to);
            return (
              <g key={index}>
                <polyline points={`${at(mark.from)} ${at(mark.to)}`} {...stroke} />
                <polyline points={`${at(left)} ${at(mark.to)} ${at(right)}`} {...stroke} />
              </g>
            );
          }
          return null;
        })}
      </svg>
      {annotation.marks.map((mark, index) =>
        mark.kind === 'text' ? (
          <span
            key={`text-${index}`}
            className="pointer-events-none absolute whitespace-nowrap font-bold leading-none"
            style={{
              left: `${mark.at[0] * 100}%`,
              top: `${mark.at[1] * 100}%`,
              color: ANNOTATION_COLOURS[mark.colour],
              fontSize: `${mark.size * 100}cqw`,
              textShadow: '0 0 2px #000',
            }}
          >
            {mark.text}
          </span>
        ) : null,
      )}
    </>
  );
}

/** A short description of the marks, for a screen reader. */
export function describeAnnotation(annotation: PhotoAnnotation | null): string {
  if (!annotation || annotation.marks.length === 0) return '';
  const texts = annotation.marks.flatMap((mark) => (mark.kind === 'text' ? [mark.text] : []));
  const drawn = annotation.marks.length - texts.length;
  return [drawn ? `${drawn} drawn mark${drawn === 1 ? '' : 's'}` : '', texts.length ? `labelled: ${texts.join('; ')}` : '']
    .filter(Boolean)
    .join(', ');
}
